import React from 'react';
import { ServicesContext, PatientService } from '~/services';
import { useServiceSubscribe } from '~/util';

const timeout = 120000;

export function TimelyActionCounter() {
    const services = React.useContext(ServicesContext);
    const [enabled, setEnabled] = React.useState(
        services.user.getSavedSearchData().savedNotificationOptions?.app?.openTA
    );
    const [counterFilters, setCounterFilters] = React.useState(
        services.user.getSavedSearchData().savedNotificationOptions?.filters
    );
    const [value, setValue] = React.useState(0);
    const retrieveInterval = React.useRef<number | undefined>(undefined);
    const events = React.useRef<EventSource | undefined>(undefined);

    useServiceSubscribe(services.user.events.savedSearchDataUpdated, () => {
        // check for toggling the enabled status from profile
        const { savedNotificationOptions } = services.user.getSavedSearchData();
        setEnabled(savedNotificationOptions?.app?.openTA);

        // check for changes to the counter filters
        if (counterFilters !== savedNotificationOptions?.filters) {
            setCounterFilters(savedNotificationOptions?.filters);
        }
    });
    // on status changes, manually update the number until things get synced up
    useServiceSubscribe(PatientService.EVENTS.timely_action_counts_updated, (val: number) => {
        setValue((x) => x + val);
    });

    const getCount = React.useCallback(async () => {
        const result = await services.api.call({ method: 'get', url: '/api/user-totals' });
        setValue(result.data.open_timely_action_count);
    }, [services.api]);

    React.useEffect(() => {
        if (enabled) {
            getCount();
            if (services.user.hasFeatureToggle('server_sent_events')) {
                const u = new URL(window.app.sse_uri);
                u.searchParams.append(
                    'topic',
                    `https://${window.location.host}/open-timely-action-count/${
                        services.user.get().id
                    }`
                );
                events.current = new EventSource(u, { withCredentials: true });
                events.current!.onmessage = (ev) => {
                    setValue(JSON.parse(ev.data).count);
                };
            } else {
                retrieveInterval.current = window.setInterval(getCount, timeout);
            }
        }
        return () => {
            window.clearInterval(retrieveInterval.current);
            events.current?.close();
        };
    }, [enabled, getCount, counterFilters, services.user]);

    if (!enabled || value === 0) return null;
    return (
        <span className="notification-counter badge bg-red text-white">
            {value > 99 ? '99+' : value}
        </span>
    );
}
